import { Card, Typography, Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { RootState } from '../../redux/store';
import { addToCartAsync, removeOneFromCartAsync } from '../../redux/reducers/shoppingCartReducer';
import { ServiceItem } from 'orderme-api-integration-client';
import { roundDoubleNumbers } from '../../helpers/currency';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getServicePrice } from '../../helpers/costCounter';
import InfoIcon from '@mui/icons-material/Info';
import { ProductPrices } from './productPrices';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import FriendsBadge from './friendsBadge';
import { QuantityControl } from './quantityControl';

interface ProductCardProps {
  item: ServiceItem;
  backgroundColor?: string;
  fontSize: number;
  fontColor: string;
}

export function ProductCard(props: ProductCardProps) {
  const dispatch = useAppDispatch();
  const { item, backgroundColor, fontSize, fontColor } = props;
  const shoppingCartItems = useAppSelector((state: RootState) => state.shoppingCartState.items);
  const service = shoppingCartItems.find((service) => service.serviceId === item.id);
  const serviceCount: number = service?.count ?? 0;
  const isEditable = service?.removable ?? true;
  const servicePrice: number = getServicePrice(item, serviceCount);
  const [isPricesVisible, setIsPricesVisible] = useState(false);
  const { t } = useTranslation();
  const isLoggedIn = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const hasAmountPrice = item.prices?.length > 1;
  const isPriceForLoggedInUser = item.originalPrice > servicePrice;
  const showFriendsPrice = !isLoggedIn && isPriceForLoggedInUser;

  const addToBasket = () => {
    dispatch(addToCartAsync(item.id));
  };

  const removeFromBasket = () => {
    dispatch(removeOneFromCartAsync(item.id));
  };

  const togglePricesVisible = () => {
    setIsPricesVisible(!isPricesVisible);
  };

  return (
    <Card sx={{ borderRadius: '10px', mt: '10px', height: 'auto', width: '100%', backgroundColor: backgroundColor }}>
      <Grid
        container
        direction="row"
        sx={{
          width: '100%',
          margin: '15px',
          pl: '5px',
          pr: '25px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {showFriendsPrice && (
          <Box
            sx={{
              position: 'absolute',
              right: '15px',
              top: '10px',
            }}
          >
            <FriendsBadge />
          </Box>
        )}
        <Grid size="grow" container sx={{ alignItems: 'center', color: fontColor, fontSize: fontSize }}>
          {item.name}
        </Grid>
        <Grid
          size="auto"
          sx={{
            height: 'auto',
            color: fontColor,
            fontSize: fontSize,
            mx: '5px',
          }}
          textAlign="right"
        >
          <Grid container direction="column">
            {
              <Grid container direction="row" sx={{ justifyContent: 'flex-end' }}>
                <Grid
                  sx={{
                    opacity: !showFriendsPrice ? 0.7 : null,
                    color: fontColor,
                    fontSize: fontSize,
                    textDecoration: !showFriendsPrice ? 'line-through' : null,
                    pr: '15px',
                    display: item.originalPrice - servicePrice === 0 ? 'none' : 'inline-block',
                  }}
                >
                  {roundDoubleNumbers(item.originalPrice)} €
                </Grid>
                <Grid sx={{ fontWeight: showFriendsPrice ? 'bold' : null }}>{roundDoubleNumbers(servicePrice)} €</Grid>
              </Grid>
            }
          </Grid>
        </Grid>
      </Grid>
      {hasAmountPrice && (
        <Grid
          container
          direction="row"
          sx={{
            backgroundColor: 'white',
            pr: '25px',
            pt: '5px',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Typography alignItems="right" sx={{ fontSize: '14px' }}>
            {t('services:bulkDiscount')}
          </Typography>
          <InfoIcon fontSize="small" sx={{ color: 'infoIcon' }} onClick={togglePricesVisible} />
        </Grid>
      )}
      <Grid container direction="row" sx={{ justifyContent: 'space-around', backgroundColor: 'white', py: '5px' }}>
        <QuantityControl
          count={serviceCount}
          onAdd={addToBasket}
          onRemove={removeFromBasket}
          isEditable={isEditable}
          fontSize="large"
        />
      </Grid>
      {hasAmountPrice && (
        <ProductPrices
          serviceName={item.name}
          prices={item.prices}
          open={isPricesVisible}
          onClose={togglePricesVisible}
        />
      )}
    </Card>
  );
}
